import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDependencies } from '@servicetitan/react-ioc';
import { Link, Text } from '@servicetitan/anvil2';

import { MessageCard } from '../../message-card/message-card';
import { ChatStore } from '../../../stores/chat.store';
import { MessageType } from '../../../models/message-type.model';
import { scrollToBottom } from '../../../utils/scroll-to-bottom';
import { formatDate } from '../../../utils/format-date';
import { formatCurrency } from '../../../utils/format-currency';
import { EstimateItemModel, EstimateListModel } from '../../../models/estimate.model';

interface EstimateListProps {
    data: EstimateListModel;
}

export const EstimateList: FC<EstimateListProps> = ({ data }) => {
    const [showMore, setShowMore] = useState(false);

    const [chatStore] = useDependencies(ChatStore);

    const estimates = useMemo(() => {
        if (showMore) {
            return data.estimates;
        }
        return data.estimates.slice(0, 3);
    }, [showMore, data.estimates]);

    const handleShowMore = (estimate: EstimateItemModel) => {
        chatStore.enqueueMessages([
            {
                isMine: false,
                type: MessageType.RichText,
                showAvatar: true,
                text: `Here is information about the <strong>Estimate #${estimate.estimate_id}</strong>:`,
            },
            {
                isMine: false,
                type: MessageType.EstimateDetails,
                data: estimate,
            },
        ]);
    };

    const toggleShowAll = useCallback(() => {
        setShowMore(!showMore);

        if (!showMore) {
            scrollToBottom();
        }
    }, [setShowMore, showMore]);

    return (
        <div className="w-100">
            {estimates.map((estimate, index) => {
                return (
                    <MessageCard
                        key={estimate.estimate_id}
                        className={classNames(index > 0 && 'm-t-2')}
                        flexDirection="column"
                    >
                        <Text variant="headline" el="h3" size="small">
                            Estimate ID #{estimate.estimate_id} - "{estimate.estimate_name}"
                        </Text>

                        <div className="m-b-1">
                            <Text variant="eyebrow" className="m-b-half" size="small">
                                Created by
                            </Text>
                            <Text className="m-b-1" size="small">
                                {estimate.estimate_created_by}
                            </Text>
                        </div>

                        <div className="m-b-1">
                            <Text variant="eyebrow" className="m-b-half" size="small">
                                Created On
                            </Text>
                            <Text className="m-b-1" size="small">
                                {formatDate(new Date(estimate.estimate_created_date))}
                            </Text>
                        </div>

                        <div className="m-b-1">
                            <Text variant="eyebrow" className="m-b-half" size="small">
                                Estimate Total
                            </Text>
                            <Text className="m-b-1" size="small">
                                {formatCurrency(estimate.estimate_total)}
                            </Text>
                        </div>

                        <Link quiet appearance="primary" onClick={() => handleShowMore(estimate)}>
                            View more info
                        </Link>
                    </MessageCard>
                );
            })}
            {data.estimates.length > 3 && (
                <Link quiet appearance="primary" className="m-t-1" onClick={toggleShowAll}>
                    {showMore ? 'View fewer estimates' : 'View full list'}
                </Link>
            )}
        </div>
    );
};
